import { ArchiveView } from "./archive/view.js";
import { getJson } from "./net/http.js";
import { subscribe } from "./net/sse.js";
import { el } from "./ui/dom.js";
import { Grid, type CameraInfo } from "./ui/grid.js";

declare const __BUILD__: string;

/** Ответ `/auth/session`. Токенов здесь нет и быть не может. */
interface SessionInfo {
  /** Настроен ли вход вообще. `false` — сервер поднят с `--insecure-no-auth`. */
  enabled: boolean;
  signed_in: boolean;
  username: string | null;
  /** "viewer" | "admin" | "forbidden" */
  access: string;
}

function show(id: string): void {
  for (const screen of document.querySelectorAll<HTMLElement>(".screen")) {
    screen.hidden = screen.id !== id;
  }
}

/**
 * Вход — обычный переход страницы, а не fetch.
 *
 * Keycloak отвечает редиректом на свою форму, и XHR его не отработает:
 * кросс-доменный редирект в fetch выглядит сетевой ошибкой.
 */
function signIn(): void {
  location.assign("/auth/login");
}

/**
 * Выход — POST, а не ссылка.
 *
 * GET'ом выход делается одним `<img src>` с чужой страницы, и это ровно та
 * CSRF, ради которой выход переводят на POST.
 */
function signOut(): void {
  const form = document.createElement("form");
  form.method = "post";
  form.action = "/auth/logout";
  form.hidden = true;
  document.body.appendChild(form);
  form.submit();
}

/**
 * Показать, чем кончилась прошлая попытка входа, и убрать след из адреса.
 *
 * Оставлять `auth_error` в адресной строке нельзя: перезагрузка показала бы ту
 * же ошибку ещё раз, уже без всякого отношения к происходящему.
 */
function consumeAuthError(): string | null {
  const params = new URLSearchParams(location.search);
  const error = params.get("auth_error");
  if (error === null) return null;

  params.delete("auth_error");
  const rest = params.toString();
  history.replaceState(null, "", location.pathname + (rest ? `?${rest}` : ""));
  return error;
}

class App {
  private readonly grid = new Grid(el("cameras"));
  private archive: ArchiveView | null = null;
  private cameras: CameraInfo[] = [];

  async start(): Promise<void> {
    el("tab-live").addEventListener("click", () => this.openTab("live"));
    el("tab-archive").addEventListener("click", () => this.openTab("archive"));

    await this.refreshCameras();
    this.grid.render(this.cameras);

    // Поток событий: пропавшая камера должна быть видна за секунду, без опроса.
    subscribe((kind) => {
      if (kind === "camera") void this.refreshCameras().then(() => {
        this.grid.render(this.cameras);
        this.showBanner();
      });
    });

    // Подстраховка на случай, если поток событий не поднялся: раз в минуту
    // всё равно спрашиваем. Дешевле, чем объяснять пустую сетку.
    window.setInterval(() => {
      void this.refreshCameras().then(() => {
        this.grid.render(this.cameras);
        this.showBanner();
      });
    }, 60_000);

    this.showBanner();

    // Пришли по ссылке на место в архиве — открываем его, а не живой просмотр.
    if (location.hash.startsWith("#archive")) this.openTab("archive");
  }

  private async refreshCameras(): Promise<void> {
    try {
      const body = await getJson<{ cameras: CameraInfo[] }>("/api/cameras");
      this.cameras = body.cameras;
    } catch (err) {
      console.warn("не получить список камер", err);
    }
  }

  /** Критическое состояние хотя бы одной камеры показываем на всю страницу. */
  private showBanner(): void {
    const offline = this.cameras.filter((camera) => camera.state === "offline");
    const banner = el("banner");
    if (offline.length === 0) {
      banner.hidden = true;
      return;
    }
    const names = offline.map((c) => c.display_name || c.name).join(", ");
    // Сухо, без догадок о причине: отличить обрыв связи от обесточивания или
    // кражи отсюда нельзя, и гадать на странице незачем.
    banner.textContent = `Ошибка связи: ${names}`;
    banner.hidden = false;
  }

  private openTab(which: "live" | "archive"): void {
    const live = which === "live";
    el("view-live").hidden = !live;
    el("view-archive").hidden = live;
    el("tab-live").className = live ? "tab tab-active" : "tab";
    el("tab-archive").className = live ? "tab" : "tab tab-active";

    if (live) {
      this.archive?.hide();
      history.replaceState(null, "", location.pathname + location.search);
      return;
    }
    const hash = location.hash.startsWith("#archive") ? location.hash.slice(1) : null;
    this.archive ??= new ArchiveView();
    void this.archive.show(hash);
  }
}

async function restoreSession(): Promise<void> {
  const authError = consumeAuthError();
  if (authError !== null) {
    const box = el("signin-error");
    box.textContent = `Войти не удалось: ${authError}`;
    box.hidden = false;
  }

  let session: SessionInfo;
  try {
    session = await getJson<SessionInfo>("/auth/session");
  } catch {
    // Сервер недоступен или отвечает не тем — показываем вход: это
    // единственное действие, которое отсюда вообще имеет смысл.
    show("screen-signin");
    return;
  }

  if (session.enabled && !session.signed_in) {
    show("screen-signin");
    return;
  }

  if (session.enabled && session.access === "forbidden") {
    show("screen-no-access");
    return;
  }

  const suffix = session.access === "admin" ? " · администратор" : "";
  el("whoami").textContent = session.enabled
    ? (session.username ?? "") + suffix
    : "вход выключен";
  show("screen-main");

  await new App().start();
}

function main(): void {
  console.info(`cctv web, сборка ${__BUILD__}`);
  el("signin").addEventListener("click", signIn);
  el("signout").addEventListener("click", signOut);
  void restoreSession();
}

main();
